'use client'

import { BankFeedsAddNewFeed } from '@repo/accounts/BankFeedsAddNewFeed'
import { BankFeedsSubTitleModule } from '@repo/accounts/BankFeedsSubTitleModule'
import { confirm } from '@repo/ui/Confirm'
import { formatCurrency } from '@repo/utils/numbers'
import { OverflowIcon } from '@repo/ui/OverflowIcon'
import { RefreshIcon } from '@repo/ui/RefreshIcon'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useBankFeedsStore } from '@repo/store/useBankFeedsStore'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material'
import MUIButton from '@mui/material/Button'
import MUIPaper from '@mui/material/Paper'
import MUIPopover from '@mui/material/Popover'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'

export const BankFeedsCard = ({
  bankFeed,
  currentBankFeedPaymentLinkId,
  isCreatePaymentLinkOpen,
  setCreatePaymentLinkOpen,
  setCurrentBankFeedPaymentLinkId,
  testData = false
}: BankFeedsCardProps) => {
  const { roleId } = useAuthenticationStore()
  const {
    deleteBankFeed,
    fetchCurrentBankFeedBalance,
    resetOspreyBankAccounts,
    setBankFeedsHaveChanged,
    setSingleBankFeed
  } = useBankFeedsStore()

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [editBankFeedIsOpen, setEditBankFeedIsOpen] = useState(false)

  const navigate = useNavigate()

  const closeMenuPopover = () => setMenuAnchorEl(null)

  const openMenuPopover = Boolean(menuAnchorEl)

  const theme = useTheme()

  const handleDeleteBankFeed = async ({ plaidBankAccountId }: any) => {
    if (testData) return
    const proceed = await confirm(
      'Are you sure you want to delete this bank feed? This will remove the account and you will no longer be able to see transactions or create payment links.',
      { delete: true }
    )

    if (!proceed) return

    if (proceed) {
      try {
        await resetOspreyBankAccounts()
        await deleteBankFeed({ plaidBankAccountId: plaidBankAccountId })
        await setBankFeedsHaveChanged()
        closeMenuPopover()
      } catch (error) {
        console.error('Error deleting bank feed:', error)
      }
    }
  }

  const handleNavigateToTransactions = () => {
    if (testData) return

    setSingleBankFeed({ bankFeed: bankFeed })
    navigate(`/accounts/banks-journals/bank-feeds/${bankFeed.plaidBankAccountId}`)
  }

  useEffect(() => {
    if (testData) return

    const fetchCurrentBalance = async () => {
      try {
        await fetchCurrentBankFeedBalance({
          plaidBankAccountId: bankFeed.plaidBankAccountId
        })
      } catch (error) {
        console.error('Error fetching current balance for bank feed:', error)
      }
    }

    fetchCurrentBalance()
  }, [bankFeed?.plaidBankAccountId])

  return (
    <MUIPaper
      data-testid="BankFeedsCard"
      sx={{
        backgroundColor: theme.palette.primary.contrastText,
        maxWidth: '350px',
        width: '100%',
        minHeight: '150px',
        p: '20px'
      }}
    >
      <MUIStack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ width: '100%' }}>
        <MUITypography
          variant="h6"
          onClick={handleNavigateToTransactions}
          sx={{
            cursor: 'pointer',
            fontSize: '1.125rem',
            m: 0,
            p: '1rem 1rem 0',
            transition: 'color 0.3s ease-in-out',
            '&:hover': { color: theme.palette.secondary.dark, transition: 'color 0.3s ease-in-out' }
          }}
        >
          {bankFeed?.ospreyName ?? ''}
        </MUITypography>
        <MUIButton
          onClick={(e: any) => setMenuAnchorEl(e.currentTarget)}
          variant="text"
          sx={{ height: '1.25rem', width: '1.25rem', m: '0.5rem 0.5rem 0.25rem', p: '1rem', minWidth: 'unset' }}
        >
          <OverflowIcon sx={{ height: '1.25rem', width: '1.25rem', m: '1rem 0.75rem' }} />
        </MUIButton>
      </MUIStack>
      <BankFeedsSubTitleModule bankFeed={bankFeed} />
      <hr
        style={{
          width: 'calc(100% - 4rem)',
          margin: '1rem 3rem 1rem 1rem',
          border: '1px solid rgba(5, 28, 51, 0.06)'
        }}
      />
      {/* <MUIStack direction="row" alignItems="flex-start" justifyContent="flex-start">
        <MUITypography variant="h6" sx={{ p: '0.25rem 0.5rem 0 1rem', textTransform: 'uppercase' }}>
          Last sync
        </MUITypography>
        <MUIButton
          onClick={refreshBankFeedTransactions}
          variant="text"
          sx={{ height: '1.25rem', width: '1.25rem', minWidth: 'unset', mt: '3px' }}
        >
          <RefreshIcon sx={{ height: '1.25rem', width: '1.25rem' }} />
        </MUIButton>
      </MUIStack>
      <MUITypography variant="body2" sx={{ p: '0.25rem 1rem 0' }}>
        {bankFeed.currentLastSync || bankFeed.lastSync || 'Not synced yet'}
      </MUITypography> */}
      <MUITypography variant="h6" sx={{ p: '0 1rem 1rem 1rem', m: 0, textTransform: 'uppercase' }}>
        Balance
      </MUITypography>
      <MUITypography variant="body2" sx={{ p: '0 1rem 1rem' }}>
        {bankFeed?.currentBalance === undefined || bankFeed?.currentBalance === null
          ? 'Loading...'
          : formatCurrency(bankFeed?.currentBalance)}
      </MUITypography>
      <MUIPopover
        open={openMenuPopover}
        anchorEl={menuAnchorEl}
        onClose={closeMenuPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MUIStack
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          sx={{ width: '100%', minWidth: '15rem' }}
        >
          <MUIButton
            onClick={handleNavigateToTransactions}
            variant="text"
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              m: '0',
              p: '1rem 1rem',
              width: '100%'
            }}
          >
            View transactions
          </MUIButton>
          <MUIButton
            onClick={() => {
              closeMenuPopover()
              setCurrentBankFeedPaymentLinkId(bankFeed)
              setCreatePaymentLinkOpen(!isCreatePaymentLinkOpen)
            }}
            variant="text"
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              m: '0',
              p: '1rem 1rem',
              width: '100%'
            }}
            disabled={!bankFeed.plaidRecipientId}
          >
            Create payment link
          </MUIButton>
          <hr
            style={{
              width: 'calc(100% - 2rem)',
              margin: '0 1rem',
              padding: '0',
              border: '1px solid rgba(5, 28, 51, 0.06)'
            }}
          />
          <MUIButton
            onClick={() => {
              closeMenuPopover()
              setEditBankFeedIsOpen(true)
            }}
            variant="text"
            disabled={roleId !== 1}
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              m: '0',
              p: '1rem 1rem',
              width: '100%'
            }}
          >
            Edit
          </MUIButton>
          <MUIButton
            onClick={() =>
              handleDeleteBankFeed({
                plaidBankAccountId: bankFeed?.plaidBankAccountId
              })
            }
            variant="text"
            disabled={roleId !== 1}
            sx={{
              color: theme.palette.error.main,
              justifyContent: 'flex-start',
              textTransform: 'none',
              m: '0',
              p: '1rem 1rem',
              width: '100%'
            }}
          >
            Delete
          </MUIButton>
        </MUIStack>
      </MUIPopover>
      <BankFeedsAddNewFeed
        bankFeed={bankFeed}
        editMode={true}
        isOpen={editBankFeedIsOpen}
        setOpen={setEditBankFeedIsOpen}
      />
    </MUIPaper>
  )
}

type BankFeedsCardProps = {
  bankFeed: any
  currentBankFeedPaymentLinkId: string | null
  isCreatePaymentLinkOpen: boolean
  setCreatePaymentLinkOpen: (isCreatePaymentLinkOpen: boolean) => void
  setCurrentBankFeedPaymentLinkId: (currentBankFeedPaymentLinkId: string | null) => void
  testData?: boolean
}
